import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchWithTimeout } from '../utils/networkError';
import ApiErrorAlert from '../components/ApiErrorAlert';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Loading skeleton component
const StatCardSkeleton = () => (
  <div className="glass border border-light-border dark:border-border rounded-2xl p-6 animate-pulse">
    <div className="h-3 bg-slate-200 dark:bg-silver/20 rounded w-32 mb-4"></div>
    <div className="h-8 bg-slate-200 dark:bg-silver/20 rounded w-12"></div>
  </div>
);

// Stat card component
const StatCard = ({ label, value, isLoading }) => {
  if (isLoading) {
    return <StatCardSkeleton />;
  }

  return (
    <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
      <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
        {label}
      </p>
      <p className="font-display text-3xl italic">{value}</p>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, token, loading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Dashboard data state
  const [metrics, setMetrics] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    // Redirect if not authenticated or not an admin
    if (!authLoading && (!isAuthenticated || user?.role !== 'admin')) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Fetch admin metrics
  const fetchMetrics = useCallback(async () => {
    if (!isAuthenticated || user?.role !== 'admin' || !token) {
      return;
    }

    setIsLoadingData(true);
    setApiError(null);

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/admin/metrics`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (response.status === 403) {
        // Access denied - redirect to home
        navigate('/');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch admin metrics');
      }

      const data = await response.json();
      setMetrics(data);
    } catch (error) {
      console.error('Error fetching admin metrics:', error);
      setApiError(error);
    } finally {
      setIsLoadingData(false);
    }
  }, [isAuthenticated, user, token, navigate]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-light-text dark:border-pure-white"></div>
      </div>
    );
  }

  // Don't render if not admin (redirecting)
  if (!isAuthenticated || user?.role !== 'admin') {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-display text-4xl italic tracking-tighter mb-2">
          Admin Dashboard
        </h1>
        <p className="font-body text-light-text-secondary dark:text-silver/60">
          Platform overview and management
        </p>
      </div>

      {/* API Error Alert */}
      {apiError && (
        <div className="mb-6">
          <ApiErrorAlert
            error={apiError}
            onRetry={fetchMetrics}
          />
        </div>
      )}

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          label="Total Revenue"
          value={metrics ? `£${metrics.totalRevenue?.toLocaleString() || 0}` : '£0'}
          isLoading={isLoadingData}
        />
        <StatCard
          label="Active Doctors"
          value={metrics?.activeDoctors || 0}
          isLoading={isLoadingData}
        />
        <StatCard
          label="Appointments This Month"
          value={metrics?.appointmentsThisMonth || 0}
          isLoading={isLoadingData}
        />
        <StatCard
          label="Total Patients"
          value={metrics?.totalPatients || 0}
          isLoading={isLoadingData}
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <StatCard
          label="Pending Reviews"
          value={metrics?.pendingReviews || 0}
          isLoading={isLoadingData}
        />
        <StatCard
          label="Total Reviews"
          value={metrics?.totalReviews || 0}
          isLoading={isLoadingData}
        />
        <StatCard
          label="Platform Fee Revenue"
          value={metrics ? `£${metrics.platformFeeRevenue?.toLocaleString() || 0}` : '£0'}
          isLoading={isLoadingData}
        />
      </div>

      {/* Admin Navigation Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Link to="/admin/doctors" className="glass border border-light-border dark:border-border rounded-2xl p-6 hover:border-slate-300 dark:hover:border-silver/20 transition-colors">
          <h3 className="font-mono text-sm uppercase tracking-widest mb-2">Doctors</h3>
          <p className="font-body text-light-text-secondary dark:text-silver/60 text-sm">Manage registered doctors</p>
        </Link>
        <Link to="/admin/reviews" className="glass border border-light-border dark:border-border rounded-2xl p-6 hover:border-slate-300 dark:hover:border-silver/20 transition-colors">
          <h3 className="font-mono text-sm uppercase tracking-widest mb-2">Reviews</h3>
          <p className="font-body text-light-text-secondary dark:text-silver/60 text-sm">Review moderation queue</p>
        </Link>
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6 hover:border-slate-300 dark:hover:border-silver/20 transition-colors opacity-60 cursor-not-allowed">
          <h3 className="font-mono text-sm uppercase tracking-widest mb-2">Transactions</h3>
          <p className="font-body text-light-text-secondary dark:text-silver/60 text-sm">View all transactions (Coming soon)</p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
